import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useChatUnread } from '@/components/layout/ChatNotificationProvider'
import { clearRouteMemory } from '@/hooks/useRouteMemory'
import { useApi } from '@/hooks/useApi'
import UserMenu from './UserMenu'
import { useTheme } from '@/hooks/useTheme'
import { puedeVerRuta, tieneRecorte, type Modulos } from '@/lib/modulos'

// Badge counts for sidebar notifications
// `activo` es falso cuando el menú no muestra Hosting: el sondeo salía igual
// cada 60 segundos y volvía 403 en una sesión recortada, para pintar un badge
// que de todos modos no está.
function useHostingPendingCount(activo: boolean) {
  const api = useApi();
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!activo) return;
    let mounted = true;
    const fetchCount = async () => {
      try {
        const data = await api.get('/api/hosting/pedidos?estado=pendiente');
        if (mounted && Array.isArray(data)) setCount(data.length);
      } catch { /* silent */ }
    };
    fetchCount();
    const interval = setInterval(fetchCount, 60000); // poll every 60s
    return () => { mounted = false; clearInterval(interval); };
  }, [activo]);
  return count;
}

interface NavItem {
  to: string
  label: string
  icon: React.ReactNode
  end?: boolean
  badgeKey?: string // used to match badge counts
}

interface NavGroup {
  label: string
  icon: React.ReactNode
  basePath: string
  children: NavItem[]
}

type NavEntry = NavItem | NavGroup

function isGroup(entry: NavEntry): entry is NavGroup {
  return 'children' in entry
}

const navEntries: NavEntry[] = [
  {
    to: '/',
    label: 'Dashboard',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
      </svg>
    ),
  },
  // ─── CRM Group (Pipeline + Conversaciones + Cotizaciones) ───
  {
    label: 'CRM',
    basePath: '/crm',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/>
      </svg>
    ),
    children: [
      {
        to: '/prospeccion',
        label: 'Prospección',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
        ),
      },
      {
        to: '/pipeline',
        label: 'Pipeline',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/>
          </svg>
        ),
      },
      {
        to: '/conversaciones',
        label: 'Conversaciones',
        badgeKey: 'conversaciones',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
          </svg>
        ),
      },
      {
        to: '/cotizaciones',
        label: 'Cotizaciones',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
          </svg>
        ),
      },
    ],
  },
  // Buzón de Equipo: correo propio del ERP. Va suelto y no dentro de CRM porque
  // lo usa todo el equipo —Contabilidad primero—, no solo la gente de ventas.
  {
    to: '/buzon',
    label: 'Buzón',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-10 6L2 7"/>
      </svg>
    ),
  },
  // ─── Contabilidad Group ───
  {
    label: 'Contabilidad',
    basePath: '/contabilidad',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
      </svg>
    ),
    children: [
      {
        to: '/contabilidad/caja',
        label: 'Flujo de Caja',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/>
          </svg>
        ),
      },
      {
        to: '/facturas',
        label: 'Facturas',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/>
          </svg>
        ),
      },
      {
        to: '/contabilidad/programados',
        label: 'Pagos Programados',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><circle cx="12" cy="15" r="2"/>
          </svg>
        ),
      },
      {
        to: '/contabilidad/recurrentes',
        label: 'Pagos Recurrentes',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
          </svg>
        ),
      },
      {
        to: '/contabilidad/expedientes',
        label: 'Expedientes SUNAT',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 4a2 2 0 0 1 2-2h7l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z"/><polyline points="13 2 13 7 18 7"/><line x1="8" y1="13" x2="15" y2="13"/><line x1="8" y1="17" x2="13" y2="17"/>
          </svg>
        ),
      },
      {
        to: '/contabilidad/comprobantes',
        label: 'Comprobantes de Agenda',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1z"/><line x1="8" y1="9" x2="16" y2="9"/><line x1="8" y1="14" x2="13" y2="14"/>
          </svg>
        ),
      },
      {
        to: '/contabilidad/centrales',
        label: 'Centrales de Costo',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="6" width="20" height="14" rx="2"/><path d="M2 10h20"/><path d="M16 14h.01"/>
          </svg>
        ),
      },
    ],
  },
  // ─── Operaciones Group (Proyectos + Backlog) ───
  {
    label: 'Operaciones',
    basePath: '/operaciones',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 12l2 2 4-4"/><line x1="9" y1="7" x2="15" y2="7"/>
      </svg>
    ),
    children: [
      {
        to: '/proyectos',
        label: 'Proyectos',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 12l2 2 4-4"/>
          </svg>
        ),
      },
      {
        to: '/backlog',
        label: 'Backlog',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/><line x1="9" y1="10" x2="15" y2="10"/>
          </svg>
        ),
      },
      {
        to: '/hosting',
        label: 'Hosting',
        badgeKey: 'hosting',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="2" width="20" height="8" rx="2" ry="2"/><rect x="2" y="14" width="20" height="8" rx="2" ry="2"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/>
          </svg>
        ),
      },
      {
        to: '/redivia',
        label: 'Redivia',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9l9-6 9 6v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><path d="M9 21V12h6v9"/>
          </svg>
        ),
      },
    ],
  },
  {
    to: '/perfiles',
    label: 'Perfiles',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/>
      </svg>
    ),
  },
  {
    to: '/servicios',
    label: 'Servicios',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
      </svg>
    ),
  },
  // ─── Clientes Group (Empresas + Contactos) ───
  {
    label: 'Clientes',
    basePath: '/clientes',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    ),
    children: [
      {
        to: '/clientes',
        label: 'Empresas',
        end: true,
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
          </svg>
        ),
      },
      {
        to: '/clientes/contactos',
        label: 'Contactos',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
          </svg>
        ),
      },
      {
        to: '/clientes/partners',
        label: 'Partners',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
        ),
      },
      {
        to: '/clientes/accesos',
        label: 'Accesos',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
        ),
      },
    ],
  },
  {
    to: '/agentes',
    label: 'Agentes',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2a2 2 0 012 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 017 7v1H3v-1a7 7 0 017-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 012-2z" />
        <circle cx="8" cy="13" r="1" /><circle cx="16" cy="13" r="1" />
        <path d="M7 17h10a4 4 0 01-10 0z" />
      </svg>
    ),
  },
  {
    to: '/apps',
    label: 'Apps',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="5" y="5" width="14" height="14" rx="2"/><rect x="9" y="9" width="6" height="6"/>
        <line x1="9" y1="2" x2="9" y2="5"/><line x1="15" y1="2" x2="15" y2="5"/><line x1="9" y1="19" x2="9" y2="22"/><line x1="15" y1="19" x2="15" y2="22"/>
        <line x1="2" y1="9" x2="5" y2="9"/><line x1="2" y1="15" x2="5" y2="15"/><line x1="19" y1="9" x2="22" y2="9"/><line x1="19" y1="15" x2="22" y2="15"/>
      </svg>
    ),
  },
  // ─── Marketing Group ───
  {
    label: 'Marketing',
    basePath: '/marketing',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
      </svg>
    ),
    children: [
      {
        to: '/marketing/email',
        label: 'Email Mkt',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
          </svg>
        ),
      },
      {
        to: '/social',
        label: 'Social Media',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
            <line x1="12" y1="2" x2="12" y2="4"/>
            <path d="M3 17l4-4"/><path d="M21 17l-4-4"/>
            <circle cx="12" cy="21" r="1"/>
          </svg>
        ),
      },
      {
        to: '/marketing/agenda',
        label: 'Agendamiento',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><path d="M8 14h.01"/><path d="M12 14h.01"/><path d="M16 14h.01"/>
          </svg>
        ),
      },
      {
        to: '/marketing/flujos',
        label: 'Flujos',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
          </svg>
        ),
      },
      {
        to: '/marketing/newsletter',
        label: 'Suscriptores',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
        ),
      },
      {
        to: '/marketing/blog',
        label: 'Blog',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
          </svg>
        ),
      },
    ],
  },
  {
    to: '/wiki',
    label: 'Wiki MAPRO',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/><path d="M8 7h8"/><path d="M8 11h6"/>
      </svg>
    ),
  },
  {
    to: '/activity-log',
    label: 'Activity Log',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
      </svg>
    ),
  },
  {
    to: '/notificaciones',
    label: 'Notificaciones',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
      </svg>
    ),
  },
  {
    to: '/ui-kit',
    label: 'UI Kit',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
      </svg>
    ),
  },
  {
    to: '/marcas',
    label: 'Marcas',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/>
      </svg>
    ),
  },
  // Configuración NO está acá a propósito: vive en el menú del perfil, al pie. Ocupaba
  // un lugar fijo compitiendo con el trabajo de todos los días, y se entra a ella un
  // puñado de veces al mes. La ruta `/configuracion` sigue viva igual.
]

/* ─── Chevron icon for accordion toggle ─── */
const ChevronDown = ({ className }: { className?: string }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" className={className}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
)

/* ─── Collapse toggle arrow ─── */
const CollapseArrow = ({ collapsed }: { collapsed: boolean }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round"
    style={{ transform: collapsed ? 'rotate(180deg)' : 'none', transition: 'transform 0.25s ease' }}>
    <polyline points="15 18 9 12 15 6" />
  </svg>
)

const LS_KEY = 'sidebar_collapsed'

/**
 * El menú que le toca a esta sesión. Sin recorte devuelve el mismo array de
 * siempre; con recorte deja sólo lo visible — y un grupo que se queda sin
 * hijos desaparece entero, para no dejar un acordeón que al abrirlo no tiene
 * nada adentro.
 */
function menuPara(modulos: Modulos): NavEntry[] {
  if (!tieneRecorte(modulos)) return navEntries
  return navEntries.flatMap<NavEntry>(entry => {
    if (!isGroup(entry)) {
      return puedeVerRuta(modulos, entry.to) ? [entry] : []
    }
    const hijos = entry.children.filter(h => puedeVerRuta(modulos, h.to))
    return hijos.length > 0 ? [{ ...entry, children: hijos }] : []
  })
}

export default function Sidebar() {
  const [tema] = useTheme()
  const { user, signOut, modulos } = useAuth()
  const { unreadCount } = useChatUnread()
  const hostingPending = useHostingPendingCount(puedeVerRuta(modulos, '/hosting'))
  const navigate = useNavigate()
  const location = useLocation()

  /* ─── Sidebar collapse state (persisted) ─── */
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(LS_KEY) === '1' } catch { return false }
  })

  const toggleCollapse = useCallback(() => {
    setCollapsed(prev => {
      const next = !prev
      try { localStorage.setItem(LS_KEY, next ? '1' : '0') } catch { /* noop */ }
      return next
    })
  }, [])

  /* ─── Configuración se abre con el menú plegado ───
   *
   * Configuración tiene su propio menú lateral, y dos menús uno al lado del otro no
   * dejan lugar para el contenido. Al entrar, este pasa a íconos; al salir vuelve a lo
   * que el usuario había elegido.
   *
   * Se mueve `collapsed` SIN tocar `localStorage`, y por eso son dos cosas distintas:
   * la preferencia guardada es lo que el usuario eligió, y el plegado de Configuración
   * es prestado. Si se pisara la preferencia, entrar una vez dejaría el menú plegado
   * para siempre y parecería un bug. Como sólo se mueve el estado, el botón de plegar
   * sigue funcionando normal acá adentro: quien quiera el menú abierto en Configuración
   * lo abre, y nadie le pelea el control.
   */
  const enConfiguracion = location.pathname.startsWith('/configuracion')

  useEffect(() => {
    if (enConfiguracion) {
      setCollapsed(true)
      return
    }
    try { setCollapsed(localStorage.getItem(LS_KEY) === '1') } catch { setCollapsed(false) }
  }, [enConfiguracion])

  /* El menú de esta sesión: el completo, o el recortado por módulos. */
  const entradas = useMemo(() => menuPara(modulos), [modulos])

  /* ─── Accordion state: set of open group basePaths ─── */
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => {
    // On mount, auto-expand groups that are currently active
    const set = new Set<string>()
    entradas.forEach(entry => {
      if (isGroup(entry)) {
        const childPaths = entry.children.map(c => c.to)
        const active = location.pathname.startsWith(entry.basePath) ||
          childPaths.some(p => location.pathname === p || location.pathname.startsWith(p + '/'))
        if (active) set.add(entry.basePath)
      }
    })
    return set
  })

  /* Auto-sync accordion: open active group, close inactive groups */
  useEffect(() => {
    setOpenGroups(() => {
      const next = new Set<string>()
      entradas.forEach(entry => {
        if (isGroup(entry)) {
          const childPaths = entry.children.map(c => c.to)
          const active = location.pathname.startsWith(entry.basePath) ||
            childPaths.some(p => location.pathname === p || location.pathname.startsWith(p + '/'))
          if (active) next.add(entry.basePath)
        }
      })
      return next
    })
  }, [location.pathname, entradas])

  const toggleGroup = (basePath: string) => {
    setOpenGroups(prev => {
      const next = new Set(prev)
      if (next.has(basePath)) next.delete(basePath)
      else next.add(basePath)
      return next
    })
  }

  async function handleLogout() {
    clearRouteMemory()
    await signOut()
    navigate('/login', { replace: true })
  }

  return (
    <aside className={`sidebar${collapsed ? ' sidebar--collapsed' : ''}`} id="sidebar">
      <div className="sidebar-brand">
        <img
          src={`/assets/brand/logo-${collapsed ? 'icon' : 'full'}-${tema === 'dark' ? 'white' : 'color'}.svg`}
          alt={collapsed ? '1C' : '1conecta'}
          height="22"
        />
      </div>

      {/* ─── Collapse toggle (floats on sidebar edge) ─── */}
      <button
        className="sidebar-collapse-btn"
        onClick={toggleCollapse}
        title={collapsed ? 'Expandir menú' : 'Colapsar menú'}
        id="sidebar-collapse-btn"
      >
        <CollapseArrow collapsed={collapsed} />
      </button>

      <nav className="sidebar-nav">
        {entradas.map((entry, idx) => {
          if (isGroup(entry)) {
            const childActive = entry.children.some(c =>
              c.end ? location.pathname === c.to : (location.pathname === c.to || location.pathname.startsWith(c.to + '/'))
            )
            const headerActive = location.pathname === entry.basePath && !childActive
            const isOpen = openGroups.has(entry.basePath)

            return (
              <div key={idx} className={`nav-group${isOpen ? ' nav-group--open' : ''}`}>
                <div
                  className={`nav-item nav-group-header${headerActive ? ' active' : ''}`}
                  onClick={() => {
                    // Navigate to group's default page
                    navigate(entry.basePath)
                    // Ensure accordion is open
                    setOpenGroups(prev => {
                      const next = new Set(prev)
                      next.add(entry.basePath)
                      return next
                    })
                  }}
                  title={collapsed ? entry.label : undefined}
                >
                  {entry.icon}
                  <span className="nav-label">{entry.label}</span>
                  {/* Group-level badge: sum of children badges */}
                  {entry.basePath === '/operaciones' && hostingPending > 0 && (
                    <span className="nav-unread-badge">{hostingPending > 99 ? '99+' : hostingPending}</span>
                  )}
                  {entry.basePath === '/crm' && unreadCount > 0 && (
                    <span className="nav-unread-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
                  )}
                  <span
                    className="nav-group-chevron-btn"
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleGroup(entry.basePath)
                    }}
                    title={isOpen ? 'Colapsar' : 'Expandir'}
                  >
                    <ChevronDown className={`nav-group-chevron${isOpen ? ' nav-group-chevron--open' : ''}`} />
                  </span>
                </div>
                <div className="nav-group-children" style={{ maxHeight: isOpen ? `${entry.children.length * 40}px` : '0' }}>
                  {entry.children.map((child) => (
                    <NavLink
                      key={child.to}
                      to={child.to}
                      end={child.end}
                      className={({ isActive }) =>
                        `nav-item nav-sub-item${isActive ? ' active' : ''}`
                      }
                      title={collapsed ? child.label : undefined}
                    >
                    {child.icon}
                      <span className="nav-label">{child.label}</span>
                      {child.badgeKey === 'hosting' && hostingPending > 0 && (
                        <span className="nav-unread-badge">{hostingPending > 99 ? '99+' : hostingPending}</span>
                      )}
                      {child.badgeKey === 'conversaciones' && unreadCount > 0 && (
                        <span className="nav-unread-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
                      )}
                    </NavLink>
                  ))}
                </div>
              </div>
            )
          }
          return (
            <NavLink
              key={entry.to}
              to={entry.to}
              end={entry.to === '/'}
              className={() => {
                const isActive = entry.to === '/'
                  ? location.pathname === '/'
                  : location.pathname === entry.to || location.pathname.startsWith(entry.to + '/')
                return `nav-item${isActive ? ' active' : ''}`
              }}
              title={collapsed ? entry.label : undefined}
            >
              {entry.icon}
              <span className="nav-label">{entry.label}</span>

            </NavLink>
          )
        })}
      </nav>
      <div className="sidebar-footer">
        {user && (
          <UserMenu
            nombre={user.nombre}
            email={user.email ?? ''}
            rol={user.role}
            fotoUrl={user.foto_url}
            colapsado={collapsed}
            onCerrarSesion={handleLogout}
            // Configuración es la empresa, las integraciones y los canales:
            // no es de una sesión recortada. El guard de rutas ya la niega;
            // esto saca el enlace, para no ofrecer una puerta que rebota.
            enlaces={tieneRecorte(modulos) ? [] : undefined}
          />
        )}
        <div className="sidebar-footer-text">
          <span className="nav-label">v2.0 — Cloud</span>
        </div>
      </div>
    </aside>
  )
}
